import type { ConvexUser } from '@packages/shared';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { Authenticated, AuthLoading, Unauthenticated } from 'convex/react';
import { Toaster } from 'sonner';
import { LoadingIcon } from '@/components/LoadingIcon';
import { SignIn } from '@/features/auth/SignIn';
import type { BreadcrumbData } from '@/routes/admin/Breadcrumbs';

// Other things could be added to the router context, but we only need the convex user for now.
export type AppRouterContext = {
  /** The signed-in user (`userType: null` while signed out), from the server. */
  convexUser: ConvexUser;
};

// Augment TanStack Router's StaticDataRouteOption to include breadcrumbs
// https://tanstack.com/router/latest/docs/framework/react/guide/static-route-data#enforcing-static-data
declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    breadcrumbs?: BreadcrumbData[];
    /** Render this route flush to the content area — no padding on the layout's `<main>`. */
    noPagePadding?: boolean;
  }
}

// Note use of `createRootRouteWithContext({ ..})`, rather than `createRootRoute({ ..})`
export const Route = createRootRouteWithContext<AppRouterContext>()({
  component: RootComponent,
});

function RootComponent() {
  return (
    <>
      <AuthLoading>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingIcon />
        </div>
      </AuthLoading>
      <Unauthenticated>
        <SignIn />
      </Unauthenticated>
      <Authenticated>
        <Outlet />
      </Authenticated>
      {/* https://sonner.emilkowal.ski/toaster#api-reference */}
      <Toaster richColors={true} duration={6000} />
    </>
  );
}
